import { EurekaPackageType, type IPackageType } from '@eureka/services/src/apis/common.api.types';
import { accountDeferred, isUserLogin } from '@eureka/utils/account';
import { packageInfoDeferred } from '@eureka/utils/package-info';
import { createSharedComposable, tryOnBeforeMount } from '@vueuse/core';
import { computed, ref } from 'vue';
import type { IStripeSubscription } from '../types';
import { PAID_STATUS, ESubscriptionStatus } from '../types';
import _ from 'lodash';
import { cancelSubscription, getInvoices, resumeSubscription, createPaymentSession, checkPaymentStatus, getRechargeEligibility, getStripeSubscriptions } from '../apis/stripe-api';
import type { Account } from '@patsnap-utils/biz';
import { controlDeferred } from '@eureka/utils/control';
import type { IControl } from '@eureka/utils/control';
import { checkJpEnv } from '../utils/check-jp-env';

export const usePlg = createSharedComposable(() => {
    const packageInfo = ref<IPackageType[]>([])
    const control = ref<IControl>()
    const isLogined = ref<boolean>(false)

    // 检测套餐付款状态
    function getPaidStatus(eureka_packages: string[], beforeCheck?: (pkgs: EurekaPackageType[]) => boolean) {
        // 获取已购商业套餐
        const commercialPkgs = _.filter(packageInfo.value, (pkg) => pkg.package_type === 'COMMERCIAL').map(pkg => pkg.eureka_package) as EurekaPackageType[];

        // 前置检查，如果已购套餐中存在比当前高的版本，则跳出检查，返回低版本标识
        if (beforeCheck?.(commercialPkgs)) {
            return PAID_STATUS.LOW_VERSION;
        }
        // 获取交集套餐
        const intersectionPkgs = _.intersection(eureka_packages, commercialPkgs);
        return intersectionPkgs.length > 0 ? PAID_STATUS.PAID : PAID_STATUS.UNPAID;
    }

    const subscriptionList = ref<IStripeSubscription[]>([]);

    /**
     * 获取用户订阅列表
     */
    async function fetchSubscriptionList() {
        isLogined.value = await isUserLogin();
        const res = isLogined.value ? await getStripeSubscriptions() : [];
        subscriptionList.value = res.filter(item => [ESubscriptionStatus.active, ESubscriptionStatus.trialing].includes(item.subscription_status));
    }

    /**
     *
     * @returns 查看账单
     */
    async function handleViewInvoices(subscription_id: string) {
        if (!subscription_id) return;
        const res = await getInvoices({
            subscription_id,
            return_url: window.location.href,
        });
        if (!res) return;
        window.open(res, '_blank', 'noopener,noreferrer');
    }

    /**
     * 取消订阅
     * @returns
     */
    async function handleCancelPlan(subscription_id: string, reason: string, otherReason?: string) {
        if (!subscription_id) return;
        await cancelSubscription({
            subscription_id,
            reason,
            ...(otherReason ? { remark: otherReason } : {}),
        });
    }

    /**
     * 恢复订阅
     * @returns
     */
    async function handleResumePlan(subscription_id: string) {
        if (!subscription_id) return;
        await resumeSubscription({
            subscription_id,
        });
    }

    /**
     * 是否取消了订阅
     */
    const isCanceled = computed(() => {
        return !!subscriptionList.value?.some(item => item.cancel_at);
    })

    const account = ref<Account | null>(null);

    const isPersonal = computed(() => {
        return !account.value?._info?.company_id;
    })

    const isCompany = computed(() => {
        return account.value?._info?.company_id;
    })

    const isJPEnv = computed(() => {
        return checkJpEnv();
    })

    // 以下是套餐
    const isPro = ref<boolean>(false);

    const isAgentPersonalCommercial = computed(() => {
        return packageInfo.value.some(pkg => pkg.eureka_package === EurekaPackageType.AGENT_PERSONAL_COMMERCIAL);
    })

    const isFreemium = computed(() => {
        return control.value?.freemium;
    })

    const isCreditsAvailable = computed(() => {
        return control.value?.credit_package;
    })

    const isCountPackageAvailable = computed(() => {
        return control.value?.count_package;
    })

    const isEnterprise = computed(() => {
        return !isFreemium.value && !isPro.value && isLogined.value;
    })

    function handleCheckPaymentStatus(timestamp: string) {
        return checkPaymentStatus(timestamp);
    }
    // 调用recharge-info接口
    const rechargeInfo = ref<any>(null);

    tryOnBeforeMount(async() => {
        isLogined.value = await isUserLogin();
        if (isLogined.value) {
            packageInfo.value = await packageInfoDeferred.promise;
        }
        const res = await getRechargeEligibility();
        rechargeInfo.value = res;
        isPro.value = !!res.data.has_pay_packages;
        console.log('tryOnBeforeMount account', account.value);
        account.value = await accountDeferred.promise;

        control.value = await controlDeferred.promise;
    })

    return {
        getPaidStatus,
        packageInfo,
        rechargeInfo,
        fetchSubscriptionList,
        handleViewInvoices,
        handleCancelPlan,
        handleResumePlan,
        isPersonal,
        isEnterprise,
        isCanceled,
        subscriptionList,
        createPaymentSession,
        isPro,
        account,
        control,
        isFreemium,
        handleCheckPaymentStatus,
        isLogined,
        isCompany,
        isJPEnv,
        isCreditsAvailable,
        isCountPackageAvailable,
        isAgentPersonalCommercial,
    }
})
